import type { components } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

export const REPORT_SLUGS = [
  'headcount',
  'joiners-leavers',
  'turnover',
  'roster-team-member',
  'roster-employment',
  'roster-compensation',
  'roster-bank',
  'roster-contact',
  'roster-emergency',
  'birthdays',
  'probation-contract',
  'document-expiry',
  'demographics',
  'org-hygiene',
  'attendance-sheet',
  'attendance-summary',
  'work-hours',
  'absence-detail',
  'attendance-exceptions',
  'holiday-roll',
  'leave-balances',
  'leave-taken',
  'time-off-review',
  'absence-coverage',
  'payroll-summary',
  'payroll-register',
  'payroll-variance',
  'payroll-deductions',
  'leave-liability',
  'expense-claims',
  'expense-ageing',
  'asset-custody',
  'asset-exceptions',
  'cash-custody-balances',
  'review-completion',
  'score-distribution',
  'sessions',
  'violations',
  'permission-requests',
  'lateness',
  'overtime',
] as const

/**
 * The five reports Payroll owns rather than HR.
 *
 * They are listed on HR's reports index and reached through `/hr/reports/*`
 * routes because that is where the user looks for them, but the module that
 * computes them is Payroll and its controller answers on `/payroll/reports/*`.
 * HR's controller 404s on these slugs, which is exactly what every one of them
 * did in the UI until this map existed. The route a report reads from is a
 * property of the report, not of the screen showing it.
 */
const PAYROLL_REPORTS = new Set<string>([
  'payroll-summary',
  'payroll-register',
  'payroll-variance',
  'payroll-deductions',
  'leave-liability',
])

/** `hr` or `payroll` — the module prefix the report's endpoints live under. */
function reportModule(report: HrReportName): 'hr' | 'payroll' {
  return PAYROLL_REPORTS.has(report) ? 'payroll' : 'hr'
}

export type HrReportName = (typeof REPORT_SLUGS)[number]

export interface HrReportSearch {
  from?: string
  to?: string
  as_at?: string
  department_id?: string
  employee_id?: string
  status?: string
  employment_type?: string
  preset?: string
  compare_prior?: boolean
  sort?: string
  direction?: 'asc' | 'desc'
  page?: number
  per_page?: number
  month?: string
  leave_type_id?: string
  grain?: 'department' | 'employee'
  run_id?: string
  category_id?: string
  cycle_id?: string
  component_id?: string
  variance_threshold_percent?: string
}

/**
 * A column as the API declares it. The backend owns the column set — it is the
 * only side that knows what its query actually returns — and ships a localized
 * `label` so a report can gain a column without a frontend release. The screen
 * still prefers its own `columns.{key}` translation when it has one, because
 * nothing sets the API's locale per request and the server label is therefore
 * always English.
 */
type GeneratedHrReport = components['schemas']['HrReportResource']

export type HrReportColumn = GeneratedHrReport['columns'][number]

/**
 * A figure that describes the whole report rather than one of its rows.
 *
 * Headcount's opening/hires/leavers/closing and org hygiene's missing-field
 * counts. They used to arrive as extra `rows`, where every dimension column was
 * empty: a five-person company's headcount rendered as nine lines, four of them
 * a row of em-dashes and a number.
 */
export type HrReportTotal = GeneratedHrReport['totals'][number]

/**
 * One report as the screen reads it: the contract's `HrReportResource` with its
 * rows widened to `unknown` cells, since the columns differ per report.
 * `meta` is optional because `unwrap` accepts a body without one; `from`, `to`
 * and `as_at` are null when the report has no such period.
 */
export type HrReportPayload = Omit<GeneratedHrReport, 'meta' | 'rows'> & {
  rows: Array<Record<string, unknown>>
  meta?: GeneratedHrReport['meta']
}

export function hrReportSearchFrom(search: Record<string, unknown>): HrReportSearch {
  const number = (value: unknown): number | undefined => {
    const parsed = typeof value === 'string' ? Number(value) : value
    return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
  }
  return {
    from: typeof search.from === 'string' && search.from ? search.from : undefined,
    to: typeof search.to === 'string' && search.to ? search.to : undefined,
    as_at: typeof search.as_at === 'string' && search.as_at ? search.as_at : undefined,
    department_id:
      typeof search.department_id === 'string' && search.department_id
        ? search.department_id
        : undefined,
    employee_id:
      typeof search.employee_id === 'string' && search.employee_id ? search.employee_id : undefined,
    status: typeof search.status === 'string' && search.status ? search.status : undefined,
    employment_type:
      typeof search.employment_type === 'string' && search.employment_type
        ? search.employment_type
        : undefined,
    preset: typeof search.preset === 'string' && search.preset ? search.preset : undefined,
    compare_prior: search.compare_prior === true || search.compare_prior === '1' ? true : undefined,
    sort: typeof search.sort === 'string' && search.sort ? search.sort : undefined,
    direction:
      search.direction === 'desc' ? 'desc' : search.direction === 'asc' ? 'asc' : undefined,
    page: number(search.page),
    per_page: number(search.per_page),
    month: typeof search.month === 'string' && search.month ? search.month : undefined,
    leave_type_id:
      typeof search.leave_type_id === 'string' && search.leave_type_id
        ? search.leave_type_id
        : undefined,
    grain: search.grain === 'employee' || search.grain === 'department' ? search.grain : undefined,
    run_id: typeof search.run_id === 'string' && search.run_id ? search.run_id : undefined,
    category_id:
      typeof search.category_id === 'string' && search.category_id ? search.category_id : undefined,
    cycle_id: typeof search.cycle_id === 'string' && search.cycle_id ? search.cycle_id : undefined,
    component_id:
      typeof search.component_id === 'string' && search.component_id
        ? search.component_id
        : undefined,
    variance_threshold_percent:
      typeof search.variance_threshold_percent === 'string' && search.variance_threshold_percent
        ? search.variance_threshold_percent
        : undefined,
  }
}

function unwrap(body: unknown): HrReportPayload {
  const envelope = body as { data?: unknown }
  const value = (envelope.data ?? body) as {
    columns?: unknown
    rows?: unknown
    totals?: unknown
    meta?: HrReportPayload['meta']
  }
  return {
    columns: Array.isArray(value.columns)
      ? value.columns.filter(
          (column): column is HrReportColumn =>
            typeof column === 'object' && column !== null && 'key' in column,
        )
      : [],
    rows: Array.isArray(value.rows)
      ? value.rows.filter(
          (row): row is Record<string, unknown> => typeof row === 'object' && row !== null,
        )
      : [],
    totals: Array.isArray(value.totals)
      ? value.totals.filter(
          (total): total is HrReportTotal =>
            typeof total === 'object' && total !== null && 'key' in total && 'value' in total,
        )
      : [],
    meta: value.meta,
  }
}

export function useHrReport(
  companyId: string | null,
  report: HrReportName,
  search: HrReportSearch,
  options?: Omit<UseQueryOptions<HrReportPayload, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<HrReportPayload, Error>({
    queryKey: ['companies', companyId, 'hr', 'reports', report, search] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async () => {
      if (!companyId) throw new Error('No active company')
      const path =
        reportModule(report) === 'payroll' ? '/payroll/reports/{report}' : '/hr/reports/{report}'
      const response = await (api.GET(
        path as never,
        {
          headers: { 'X-Company-Id': companyId },
          params: { path: { report }, query: search } as never,
        } as never,
      ) as unknown as Promise<{ error?: { message?: string }; data?: unknown }>)
      if (response.error) throw new Error(response.error.message ?? `Failed to load ${report}`)
      return unwrap(response.data)
    },
  })
}

export type HrExportFormat = 'csv' | 'pdf'

/**
 * The export endpoint's URL. The typed client cannot express it usefully — the
 * body is a file, not JSON — so the path is built here and fetched by
 * `downloadFile`, which is what carries the `X-Company-Id` header a plain
 * `<a href>` navigation cannot.
 *
 * Every filter the on-screen report is showing is forwarded, so the document
 * the user gets is the table they are looking at and not the unfiltered
 * report. `page` is deliberately NOT forwarded: a printed report is the whole
 * result set, not the page the screen happens to be on.
 */
export function hrReportExportUrl(
  report: HrReportName,
  format: HrExportFormat,
  search: HrReportSearch,
): string {
  const params = new URLSearchParams()
  params.set('format', format)
  for (const [key, value] of Object.entries(search)) {
    if (key === 'page' || key === 'per_page') continue
    if (value === undefined || value === null || value === '') continue
    params.set(key, value === true ? '1' : String(value))
  }
  return `${apiOrigin}/api/${reportModule(report)}/reports/${report}/export?${params.toString()}`
}

/** Trigger the authenticated CSV/PDF download for an HR report. */
export async function exportHrReport(
  companyId: string,
  report: HrReportName,
  format: HrExportFormat,
  search: HrReportSearch,
): Promise<void> {
  await downloadFile(hrReportExportUrl(report, format, search), `${report}.${format}`, {
    'X-Company-Id': companyId,
  })
}
